import { styled } from '@mui/system';
import heroVideo from '../../../assets/videos/hero.webm';
import heroMask from '../../../assets/heroMask.png';
import { Box, Typography } from "@mui/material";
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const HeroContainer = styled('div')({
    padding: '157px 0 24px 0',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
        padding: '87px 0 24px 0',
    }
});
const VideoContainer = styled('div')({
    width: '100%',
    height: '90%',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const HeroVideo = styled('video')({
    width: '770px',
    height: '410px',
    objectFit: 'cover',
    position: 'relative',
    zIndex: 2,
    '@media (max-width: 1200px)': {
        width: '90%',
        height: '90%',
        maxHeight: '300px',
    }
});

const HeroMask = styled('img')({
    width: '776px',
    height: '412px',
    objectFit: 'cover',
    position: 'absolute',
    zIndex: 4,
    '@media (max-width: 1200px)': {
        width: '105%',
        height: '105%',
        maxHeight: '300px',
    }
});

const HeroRectangle = styled('div')({
    width: '920px',
    height: '460px',
    position: 'relative',
    backgroundColor: '#002322',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
        width: '100%',
        height: 'auto',
        flexDirection: 'column-reverse',
        padding: '24px 0',
    }
});

const HeroMaskContainer = styled('div')({
    width: '776px',
    height: '412px',
    marginRight: '24px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
        width: '100%',
        height: '100%',
        marginRight: '0',
        marginBottom: '24px',
    }
});

const HeroDarkMask = styled('div')({
    width: '776px',
    height: '412px',
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 3,
    '@media (max-width: 1200px)': {
        display: 'none',
    }
});

const HeroTextContainer = styled('div')({
    position: 'absolute',
    left: '-118px',
    top: '127px',
    zIndex: 5,
    display: 'flex',
    flexDirection: 'column',
    gap: '48px',
    '@media (max-width: 1200px)': {
        position: 'relative',
        left: '0',
        top: '0',
        padding: '0 16px',
        gap: '24px',
    }
});

const HeroTitle = styled(Typography<'h1'>)({
    color: '#00E378',
    fontSize: '64px',
    fontWeight: '800',
    lineHeight: '91px',
    '@media (max-width: 1200px)': {
        fontSize: '48px',
        lineHeight: '64px',
    }
});

const HeroSubtitle = styled(Typography<'h2'>)({
    color: '#F1F7F7',
    fontSize: '48px',
    fontWeight: '700',
    lineHeight: '64px',
    '@media (max-width: 1200px)': {
        fontSize: '32px',
        lineHeight: '48px',
    }
});

const HeroDescriptionContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1200px)': {
        gap: '8px',
    }
});

const HeroDescription = styled(Typography<'p'>)({
    color: '#F1F7F7',
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '32px',
    '@media (max-width: 1200px)': {
        fontSize: '16px',
        lineHeight: '24px',
    }
});

const BackstageLink = styled('a')({
    color: '#F1F7F7',
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '32px',
    textDecorationLine: 'underline',
    textDecorationStyle: 'wavy',
    textDecorationSkipInk: 'auto',
    textDecorationThickness: '0.5px',
    textUnderlineOffset: '1px',
    textUnderlinePosition: 'from-font',
});

export const Hero = () => {
    const { t } = useTranslation();

    return (
        <HeroContainer>
            <VideoContainer>
                <HeroRectangle>
                    <HeroTextContainer>
                        <Box>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                            >
                                <HeroTitle component="h1">
                                    {t('hero.title')}
                                </HeroTitle>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                            >
                                <HeroSubtitle component="h2">
                                    {t('hero.subtitle')}
                                </HeroSubtitle>
                            </motion.div>
                        </Box>
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                        >
                            <HeroDescriptionContainer>
                                <HeroDescription component="p">
                                    {t('hero.description1')} <BackstageLink href="https://backstage.io/" target="_blank">
                                        Backstage ©
                                    </BackstageLink>
                                </HeroDescription>
                                <HeroDescription component="p">
                                    {t('hero.description2')}
                                </HeroDescription>
                            </HeroDescriptionContainer>
                        </motion.div>
                    </HeroTextContainer>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <HeroMaskContainer>
                            <HeroVideo src={heroVideo} autoPlay muted loop />
                            <HeroDarkMask />
                            <HeroMask src={heroMask} alt="hero mask" />
                        </HeroMaskContainer>
                    </motion.div>
                </HeroRectangle>
            </VideoContainer>
        </HeroContainer>
    );
};
